import "./header.css";
import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Dropdown, Flex, Space } from "antd";
import type { MenuProps } from "antd";
import {
  ChevronDownFill,
  HomeFill,
  HomeOutline,
  BarcodeFill,
  BarcodeOutline,
  CardFill,
  CardOutline,
  TagFill,
  TagOutline,
  PeopleFill,
  PeopleOutline,
  PersonOutline,
  ReportFill,
  ReportOutline,
  ReportPanelSVG,
  SupportFill,
  SupportOutline,
} from "../../assets";
import { useApiFetch, useAuth, useLanguage } from "../../hooks";
import {
  authActions,
  LanguageEnum,
  languagesActions,
  LanguageType,
} from "../../context";
import { RouterEnum } from "../../router";
import { API_URL } from "../../services";
import { Icon } from "../icon";
import { Modal } from "../modal";
import { EditProfile, EditProps, initialEdit } from "./EditProfile";

const HeaderLogo = () => {
  return (
    <div className="header-logo-container">
      <img src={ReportPanelSVG} className="header-logo" />
    </div>
  );
};

const HeaderNav = ({
  isTabbar = false,
  className,
}: {
  isTabbar?: boolean;
  className?: string;
}) => {
  const location = useLocation();
  const { t } = useLanguage();
  const { rol } = useAuth();

  const quickAccess: {
    icon: (isActive: boolean) => React.ReactNode;
    path: string;
    text: string;
  }[] = [
    {
      icon: (isActive) => (
        <Icon
          icon={isActive ? HomeFill : HomeOutline}
          key={RouterEnum.DASHBOARD}
        />
      ),
      path: RouterEnum.DASHBOARD,
      text: t.global.routes.dashboard,
    },
    {
      icon: (isActive) => (
        <Icon
          icon={isActive ? ReportFill : ReportOutline}
          key={RouterEnum.REPORTS}
        />
      ),
      path: RouterEnum.REPORTS,
      text: t.global.routes.transactions,
    },
    {
      icon: (isActive) => (
        <Icon
          icon={isActive ? SupportFill : SupportOutline}
          key={RouterEnum.SUPPORT}
        />
      ),
      path: RouterEnum.SUPPORT,
      text: t.global.routes.support,
    },
    ...(rol.includes("admin")
      ? [
          {
            icon: (isActive: boolean) => (
              <Icon
                icon={isActive ? PeopleFill : PeopleOutline}
                key={RouterEnum.ADMIN_USERS}
              />
            ),
            path: RouterEnum.ADMIN_USERS,
            text: t.global.routes.users,
          },
          {
            icon: (isActive: boolean) => (
              <Icon
                icon={isActive ? BarcodeFill : BarcodeOutline}
                key={RouterEnum.ADMIN_AREA01_PRODUCTS}
              />
            ),
            path: RouterEnum.ADMIN_AREA01_PRODUCTS,
            text: t.global.routes.area01Products,
          },
          {
            icon: (isActive: boolean) => (
              <Icon
                icon={isActive ? TagFill : TagOutline}
                key={RouterEnum.ADMIN_UNLINKED_EANS}
              />
            ),
            path: RouterEnum.ADMIN_UNLINKED_EANS,
            text: t.global.routes.unlinkedEans,
          },
          {
            icon: (isActive: boolean) => (
              <Icon
                icon={isActive ? CardFill : CardOutline}
                key={RouterEnum.ADMIN_CARD_INVENTORY}
              />
            ),
            path: RouterEnum.ADMIN_CARD_INVENTORY,
            text: t.global.routes.cardInventory,
          },
        ]
      : []),
  ];

  return (
    <Flex
      className={`${className} ${
        isTabbar ? "header-nav-tabbar" : "header-nav-classic"
      }`}
    >
      {quickAccess.map((nav, index) => {
        if (location.pathname === nav.path) {
          return (
            <div
              key={`$nav-${nav.text}-${index}`}
              className={`header-nav-item active ${isTabbar ? "tabbar" : ""}`}
              onClick={() => {
                window.scrollTo({
                  top: 0,
                  behavior: "smooth",
                });
              }}
            >
              <span className="header-nav-icon">{nav.icon(true)}</span>
              <small className="header-nav-text">{nav.text}</small>
            </div>
          );
        } else {
          return (
            <a
              className={`header-nav-item ${isTabbar ? "tabbar" : ""}`}
              href={nav.path}
              key={`$nav-${nav.text}-${index}`}
            >
              <span className="header-nav-icon">{nav.icon(false)}</span>
              <small className="header-nav-text">{nav.text}</small>
            </a>
          );
        }
      })}
    </Flex>
  );
};

const HeaderLanguages = () => {
  const { t, currentLanguage, dispatch } = useLanguage();
  const { changeLanguage } = languagesActions(dispatch);

  const languages: LanguageType[] = ["es", "en"];

  const items: MenuProps["items"] = languages.map((lang, index) => {
    return {
      label: (
        <button
          className="header-language-button-clean header-language-button-option"
          onClick={() => changeLanguage(lang)}
        >
          {t.global.languages[lang]}
        </button>
      ),
      key: index,
    };
  });

  return (
    <Dropdown className="header-languages" menu={{ items }} trigger={["click"]}>
      <button className="header-language-button-clean current">
        <Space>
          <p className="header-language-current-txt">{currentLanguage}</p>
          <Icon icon={ChevronDownFill} key={"header.chevron.down.fill"} />
        </Space>
      </button>
    </Dropdown>
  );
};

export const GuestHeader: React.FC = () => {
  return (
    <header className="header-container">
      <div className="header-spacer" />
      <HeaderLanguages />
    </header>
  );
};

const Header: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useLanguage();
  const { dispatch: dispatchAuth } = useAuth();

  const [showDropdown, setShowDropdown] = useState<boolean>(false);
  const [showModalEditProfile, setShowModalEditProfile] =
    useState<boolean>(false);
  const [showModalSignOut, setShowModalSignOut] = useState<boolean>(false);
  const [editProfile, setEditProfile] = useState<EditProps>(initialEdit);
  const [editProfileDisabled, setEditProfileDisabled] = useState<boolean>(true);
  const [fetchUpdatePassword, , savingProfile, editProfileError] =
    useApiFetch<{ email: string }>();

  const items: MenuProps["items"] = [
    {
      label: (
        <button
          className="header-language-button-clean header-language-button-option"
          onClick={() => setShowModalEditProfile(true)}
        >
          {t.global.editprofile.title}
        </button>
      ),
      key: "button-edit-profile",
    },
    {
      label: (
        <button
          className="header-language-button-clean header-language-button-option"
          onClick={() => setShowModalSignOut(true)}
        >
          {t.global.signOut.signOut}
        </button>
      ),
      key: "button-language",
    },
  ];

  const handleEditProfile = async () => {
    const result = await fetchUpdatePassword(API_URL.ME_PASSWORD(), {
      method: "PUT",
      body: JSON.stringify({ password: editProfile.password }),
    });

    if (!result?.data) {
      return;
    }

    setEditProfile(initialEdit);
    setShowModalEditProfile(false);
  };

  const signOut = () => {
    const { setLogin, setToken, setUser } = authActions(dispatchAuth);

    setLogin(false);
    setToken("");
    setUser(undefined);
    const keepKeys: string[] = [LanguageEnum.SET]; // Permite mantener el idioma seleccionado
    Object.keys(localStorage).forEach((key) => {
      if (!keepKeys.includes(key)) {
        localStorage.removeItem(key);
      }
    });

    navigate(RouterEnum.LOGIN);
  };

  return (
    <>
      <header className="header-container">
        <HeaderLogo />

        <div className="header-options">
          <HeaderNav />
        </div>

        <div className="header-right">
          <HeaderLanguages />

          <Dropdown
            menu={{ items }}
            trigger={["click"]}
            onOpenChange={setShowDropdown}
          >
            <button
              className={`header-nav-bg-circle ${
                showDropdown ||
                showModalEditProfile ||
                showModalSignOut ||
                location.pathname === RouterEnum.ADMIN_USERS
                  ? "bordered"
                  : ""
              }`}
            >
              <Space>
                <Icon icon={PersonOutline} key={RouterEnum.LOGIN} />
              </Space>
            </button>
          </Dropdown>
        </div>
      </header>

      <HeaderNav isTabbar={true} />

      <Modal
        open={showModalEditProfile}
        setOpen={setShowModalEditProfile}
        title={t.global.editprofile.title}
        actions={[
          {
            text: t.global.editprofile.title,
            onClick: handleEditProfile,
            disabled: editProfileDisabled || savingProfile,
          },
        ]}
      >
        <EditProfile
          edit={editProfile}
          setEdit={setEditProfile}
          setDisabled={setEditProfileDisabled}
        />
        {editProfileError && (
          <span className="header-editprofile-error">{editProfileError}</span>
        )}
      </Modal>

      <Modal
        open={showModalSignOut}
        setOpen={setShowModalSignOut}
        title={t.global.signOut.title}
        titleStyles={{
          textAlign: "center",
        }}
        actions={[{ text: t.global.signOut.okText, onClick: signOut }]}
      />
    </>
  );
};

export default Header;
